import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowUpIcon, ArrowUpRightIcon } from 'lucide-react';
import { imprintDetails, legalSections, legalUpdated, modelCredits, type LegalSection } from '../data/legal';
import { focusIn } from '../utils/reveal';
const LINK = 'underline decoration-black/20 underline-offset-[0.2em] transition-colors duration-200 ease-out hover:decoration-black focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black';
const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black';
/** Page gutters: roomy on phones (24px), the site's usual gutter on desktop */
const GUTTER = 'px-6 md:px-[1.6vw]';

/** Imprint, privacy, cookies and credits on one page; a section switcher jumps between them. */
export function Legal() {
  const reduce = useReducedMotion();
  const {
    hash
  } = useLocation();
  const [active, setActive] = useState<LegalSection['id']>(legalSections[0].id);
  const tabsRef = useRef<HTMLDivElement>(null);

  // Open at the requested section (e.g. /legal#cookies from the footer), otherwise at the top
  useEffect(() => {
    const id = hash.replace('#', '');
    const el = id ? document.getElementById(id) : null;
    if (el) el.scrollIntoView({
      block: 'start'
    });else window.scrollTo(0, 0);
  }, [hash]);

  // Highlight the section currently being read
  useEffect(() => {
    const els = legalSections.map(s => document.getElementById(s.id)).filter((e): e is HTMLElement => !!e);
    const io = new IntersectionObserver(entries => {
      const hit = entries.find(e => e.isIntersecting);
      if (hit) setActive(hit.target.id as LegalSection['id']);
    }, {
      rootMargin: '-35% 0px -60% 0px'
    });
    els.forEach(e => io.observe(e));
    return () => io.disconnect();
  }, []);

  // Phones: keep the active tab in view inside the scrollable switcher
  useEffect(() => {
    const tab = tabsRef.current?.querySelector<HTMLElement>(`[data-tab="${active}"]`);
    tab?.scrollIntoView({
      block: 'nearest',
      inline: 'center',
      behavior: reduce ? 'auto' : 'smooth'
    });
  }, [active, reduce]);
  return <main className="min-h-screen w-full bg-white font-sans text-black" data-id="element-243" data-mp="3ey70vspi9xcw">
      {/* Top bar */}
      <header className={`flex items-center justify-between py-4 md:py-6 ${GUTTER}`} data-id="element-244" data-mp="2ia0twz542svg">
        <Link to="/" className={`inline-flex items-center gap-2 rounded-full text-[0.95rem] font-bold tracking-[-0.03em] transition-opacity duration-200 ease-out hover:opacity-50 ${FOCUS}`} data-id="element-245" data-mp="2n8c4jc38d7eq">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-246" data-mp="2w9inrsx9kwbo" />
          Back to Laebel
        </Link>
        <Link to="/" aria-label="Laebel home" className={`rounded-sm ${FOCUS}`} data-id="element-247" data-mp="6kzv1mdd3hia">
          <img src="https://cdn.magicpatterns.com/uploads/iFCAPKP6Hif5VAC8rezXvk/image.png" alt="" className="h-11 w-auto invert mix-blend-multiply md:h-14" data-id="element-248" data-mp="bgf6y8rt73c2" />
        </Link>
      </header>

      {/* Title */}
      <div className={`pb-10 pt-[clamp(3.5rem,8vw,8rem)] md:pb-0 ${GUTTER}`} data-id="element-249" data-mp="bvlsdfmz2pol">
        <motion.h1 className="text-[clamp(3.5rem,7.3vw,8.75rem)] font-bold leading-[0.95] tracking-[-0.055em]" {...focusIn(0.05, reduce, {
        y: 14,
        blur: 14,
        duration: 1.1
      })} data-id="element-250" data-mp="7uicwu8u6vnw">
          Legal
        </motion.h1>
        <motion.p className="mt-5 max-w-[34rem] font-serif text-[1.25rem] font-medium leading-snug md:font-normal md:text-[1.5rem]" {...focusIn(0.15, reduce, {
        y: 8,
        blur: 8,
        duration: 1
      })} data-id="element-251" data-mp="3u0p1rcjz1nut">
          The fine print — imprint, privacy, cookies and credits, in plain language.
        </motion.p>
        <motion.p className="mt-4 text-[0.9rem] font-bold tracking-[-0.02em] text-black/40" {...focusIn(0.25, reduce, {
        y: 6,
        blur: 6,
        duration: 1
      })} data-id="element-252" data-mp="1naqyktmbb6cy">
          Last updated {legalUpdated}
        </motion.p>
      </div>

      {/* Phones: sticky, horizontally scrollable section switcher */}
      <nav aria-label="Legal sections" className="sticky top-0 z-20 border-y border-black/10 bg-white/90 backdrop-blur-md md:hidden" data-id="element-253" data-mp="1v9vt9rz6b5hu">
        <div ref={tabsRef} className="flex gap-2 overflow-x-auto px-6 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-254" data-mp="1lcr4rlhuuxxv">
          {legalSections.map(s => {
          const on = active === s.id;
          return <a key={s.id} data-tab={s.id} href={`#${s.id}`} aria-current={on ? 'true' : undefined} className={`shrink-0 whitespace-nowrap rounded-full px-4 py-2 text-[0.9rem] font-bold tracking-[-0.02em] transition-colors duration-200 ease-out ${FOCUS} ${on ? 'bg-black text-white' : 'bg-black/[0.05] text-black/70'}`} data-id="element-255" data-mp="1cf73wdnwn3ns">
                {s.title}
              </a>;
        })}
        </div>
      </nav>

      <div className={`pb-24 md:pb-32 md:pt-[clamp(4rem,8vw,8rem)] ${GUTTER}`} data-id="element-256" data-mp="52jd09rzkh9k">
        <div className="grid gap-12 md:grid-cols-[minmax(0,1fr)_minmax(0,2.4fr)] md:gap-[4vw]" data-id="element-257" data-mp="j19bntugep01">
          {/* Desktop: section index sticks alongside the text */}
          <nav aria-label="Legal sections" className="hidden md:sticky md:top-8 md:block md:self-start" data-id="element-258" data-mp="eymh3l6eepz5">
            <ul className="flex flex-col gap-2 text-[clamp(1rem,1.3vw,1.5rem)] font-bold tracking-[-0.03em]" data-id="element-259" data-mp="sxm0oef20eog">
              {legalSections.map(s => <li key={s.id} data-id="element-260" data-mp="12ogp9pfrvqd2">
                  <a href={`#${s.id}`} aria-current={active === s.id ? 'true' : undefined} className={`rounded-sm transition-opacity duration-200 ease-out hover:opacity-100 ${FOCUS} ${active === s.id ? 'opacity-100' : 'opacity-35'}`} data-id="element-261" data-mp="12op25hbwavo9">
                    {s.title}
                  </a>
                </li>)}
            </ul>
          </nav>

          <div className="max-w-[46rem]" data-id="element-262" data-mp="9dk1zpirwsru">
            {legalSections.map((section, i) => <SectionBody key={section.id} section={section} first={i === 0} data-id="element-263" data-mp="ddavw23ljp0l" data-mpi={i} />)}
          </div>
        </div>
      </div>

      {/* Closing strip */}
      <footer className={`bg-black py-14 text-white md:py-20 ${GUTTER}`} data-id="element-264" data-mp="n4fcf68x7hxj">
        <p className="text-[clamp(2rem,3.4vw,3.75rem)] font-bold leading-[1] tracking-[-0.045em]" data-id="element-265" data-mp="n69qc0lry9s4">Questions?</p>
        <a href="mailto:hey@superluxury.io" className="mt-3 inline-block rounded-sm text-[1.125rem] font-semibold tracking-[-0.02em] text-white/70 underline decoration-white/30 underline-offset-[0.2em] transition-colors duration-200 ease-out hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-266" data-mp="x2d0ntqs5eq5">
          hey@superluxury.io
        </a>
        <div className="mt-12 flex items-center justify-between border-t border-white/15 pt-6 text-[0.9rem] font-bold tracking-[-0.02em] text-white/40" data-id="element-267" data-mp="2vc255ztnni22">
          <span data-id="element-268" data-mp="2qj8xp46sxvma">Laebel © 2026</span>
          <a href="#top" onClick={e => {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: reduce ? 'auto' : 'smooth'
          });
        }} className="inline-flex items-center gap-1.5 rounded-sm transition-colors duration-200 ease-out hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white" data-id="element-269" data-mp="2ll7f0kdafybc">
            Back to top
            <ArrowUpIcon className="h-4 w-4" aria-hidden="true" data-id="element-270" data-mp="35bszo1i7tx25" />
          </a>
        </div>
      </footer>
    </main>;
}
function SectionBody({
  section,
  first
}: {
  section: LegalSection;
  first: boolean;
}) {
  return <section id={section.id} aria-labelledby={`${section.id}-title`}
  // Phones: offset for the sticky switcher; a hairline separates each section
  className={`scroll-mt-24 md:scroll-mt-8 ${first ? 'pt-10 md:pt-0' : 'mt-16 border-t border-black/10 pt-12 md:mt-[clamp(5rem,8vw,8rem)] md:border-t-0 md:pt-0'}`} data-id="element-271" data-mp="1ovzqr4labb3q">
      <h2 id={`${section.id}-title`} className="text-[clamp(2.25rem,3.4vw,3.75rem)] font-bold leading-[1] tracking-[-0.045em]" data-id="element-272" data-mp="3f13a1l3ipomz">
        {section.title}
      </h2>
      {section.intro && <p className="mt-5 font-serif text-[1.2rem] font-medium leading-snug text-black/80 md:font-normal md:text-[1.5rem]" data-id="element-273" data-mp="bk4f2fyjgftq">
          {section.intro}
        </p>}

      {section.id === 'imprint' && <dl className="mt-8 divide-y divide-black/10 rounded-2xl bg-black/[0.03] px-5 md:mt-10 md:rounded-none md:border-y md:border-black/10 md:bg-transparent md:px-0" data-id="element-274" data-mp="1sgn8dy2kwrq">
          {imprintDetails.map(d => <div key={d.label} className="grid gap-1 py-4 sm:grid-cols-[14rem_1fr] sm:gap-6" data-id="element-275" data-mp="ugmbhaoc13wb">
              <dt className="text-[0.85rem] font-bold tracking-[-0.01em] text-black/45" data-id="element-276" data-mp="za8iam7tedk7">{d.label}</dt>
              <dd className="break-words text-[1rem] font-semibold tracking-[-0.02em]" data-id="element-277" data-mp="2jx84vf41b6gr">
                {d.href ? <a href={d.href} className={LINK} {...d.href.startsWith('http') ? {
            target: '_blank',
            rel: 'noopener noreferrer'
          } : {}} data-id="element-278" data-mp="2ey59ynhrqgmi">
                    {d.value}
                  </a> : d.value}
              </dd>
            </div>)}
        </dl>}

      {section.id === 'credits' && <ul className="mt-8 md:mt-10" data-id="element-279" data-mp="1w2f42p6pt03c">
          {modelCredits.map(c => <li key={c.workUrl} className="rounded-2xl bg-black/[0.03] p-5 md:rounded-none md:border-y md:border-black/10 md:bg-transparent md:px-0 md:py-6" data-id="element-280" data-mp="2a151y71cz3sr">
              <p className="text-[0.85rem] font-bold tracking-[-0.01em] text-black/45" data-id="element-281" data-mp="2544w9x8f8326">3D model</p>
              <p className="mt-2 text-[1.0625rem] font-semibold leading-relaxed tracking-[-0.02em]" data-id="element-282" data-mp="3lhv49f2nvbh3">
                “
                <a href={c.workUrl} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-283" data-mp="179xwktt2upzg">
                  {c.work}
                </a>
                ” by{' '}
                <a href={c.authorUrl} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-284" data-mp="12d7ho9t0e0fm">
                  {c.author}
                </a>
                , licensed under{' '}
                <a href={c.licenseUrl} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-285" data-mp="tikj9xlriocv">
                  {c.license}
                </a>
                <ArrowUpRightIcon className="ml-1 inline h-4 w-4 align-[-0.1em] text-black/40" aria-hidden="true" data-id="element-286" data-mp="jk3mgk057fiw" />
              </p>
              <p className="mt-2 text-[0.95rem] leading-relaxed text-black/60" data-id="element-287" data-mp="embnx74zsv5q">{c.note}</p>
            </li>)}
        </ul>}

      {section.blocks.map((b, i) => <div key={i} className={b.heading ? 'mt-9' : 'mt-3'} data-id="element-288" data-mp="28zummkarejsm" data-mpi={i}>
          {b.heading && <h3 className="text-[1.125rem] font-bold tracking-[-0.03em] md:text-[1.25rem]" data-id="element-289" data-mp="24y97esar5tf9">{b.heading}</h3>}
          {b.paragraphs?.map(p => <p key={p} className="mt-2.5 text-[0.975rem] leading-[1.7] text-black/70 md:text-[1rem]" data-id="element-290" data-mp="3mv332mo7j48n">
              {p}
            </p>)}
          {b.items && <ul className="mt-3 space-y-2 text-[0.975rem] leading-[1.6] text-black/70 md:text-[1rem]" data-id="element-291" data-mp="frvtmx0s5fl">
              {b.items.map(it => <li key={it} className="relative pl-5 before:absolute before:left-0 before:top-[0.7em] before:h-[5px] before:w-[5px] before:rounded-full before:bg-black/25" data-id="element-292" data-mp="4jprtor1t04f">
                  {it}
                </li>)}
            </ul>}
        </div>)}
    </section>;
}